/**
 * Frontend constants & helpers for the National Selections virtual hierarchy
 * inside CompetitionsViewer.
 *
 * The virtual hierarchy is grafted at the bottom of the regular niveaux list
 * with string IDs prefixed by "nat:" so they don't collide with the integer
 * IDs of real DB rows.
 *
 *   Niveau virtuel   id = 'nat'
 *   Compétition (pays)   id = 'nat:france'
 *   Équipe (sélection)   id = 'nat:france:U17'  (or 'nat:france:senior')
 *
 * This module also hosts the Free Agents virtual niveau (id = 'fa') —
 * a flat virtual node that lists players with players.is_free_agent = 1
 * directly on click (no inner competition/equipe levels).
 */

// Senior is encoded as the literal token "senior" inside virtual equipe IDs
// to keep parsing unambiguous (the empty string would collide with split).
const SENIOR_TOKEN = 'senior';

export const VIRTUAL_NIVEAU_ID = 'nat';
export const VIRTUAL_NIVEAU_FA_ID = 'fa';

export const buildVirtualCompetitionId = (countryKey) => `nat:${countryKey}`;

export const buildVirtualEquipeId = (countryKey, ageCategory) =>
  `nat:${countryKey}:${ageCategory || SENIOR_TOKEN}`;

export const isFreeAgentVirtualId = (id) => id === VIRTUAL_NIVEAU_FA_ID;

export const isVirtualId = (id) =>
  typeof id === 'string' && (
    id === VIRTUAL_NIVEAU_ID ||
    id === VIRTUAL_NIVEAU_FA_ID ||
    id.startsWith('nat:') || id.startsWith('nat')
  );

/**
 * Parse a virtual ID into { kind, countryKey?, ageCategory? }.
 * Returns null if the input isn't a virtual ID.
 */
export const parseVirtualId = (id) => {
  if (typeof id !== 'string') return null;
  if (id === VIRTUAL_NIVEAU_ID) return { kind: 'niveau' };
  if (id === VIRTUAL_NIVEAU_FA_ID) return { kind: 'free-agent' };
  if (!id.startsWith('nat:')) return null;
  const parts = id.split(':');
  // parts = ['nat', countryKey, ageCategory?]
  if (parts.length === 2) {
    return { kind: 'competition', countryKey: parts[1] };
  }
  if (parts.length === 3) {
    const ageCategory = parts[2] === SENIOR_TOKEN ? '' : parts[2];
    return { kind: 'equipe', countryKey: parts[1], ageCategory };
  }
  return null;
};

/**
 * Returns a flagcdn URL for a country's flag, or null if no mapping.
 * `size` accepts 'w20', 'w40', 'w80', 'w160', 'w320', 'w640'...
 * Defaults to w320 (sharper render at typical card sizes).
 */
export const getCountryFlagUrl = (flagCode, size = 'w320') => {
  if (!flagCode) return null;
  return `https://flagcdn.com/${size}/${flagCode}.png`;
};

/**
 * Translate an age category to a display label using the i18n `t` function.
 * Empty ageCategory = senior.
 */
export const getAgeCategoryLabel = (ageCategory, t) => {
  if (!ageCategory) return t('competitionsViewer.nationalSelections.senior');
  return ageCategory; // U14..U20 are universal
};

/**
 * Translate a country key to the localised country name. The translations
 * already exist under `createPlayer.countries.<countryKey>` (used by the
 * SelectSelectionNational component).
 */
export const getCountryLabel = (countryKey, t) =>
  t(`createPlayer.countries.${countryKey}`, countryKey);
